import { FormKeySchema } from '../rev/sections/FormModal/types.js';
import { urlSchema } from '../schemas/common/url.js';
import { z } from 'zod';
import { VehicleCategory } from './VehicleTypes.js';

// ============================================================================
// CTA Action Types
// ============================================================================
/**
 * CTA action types
 */
const CtaActionType = {
    MODAL: 'modal',
    URL: 'url',
    /** Triggers the checkout flow. Use `checkoutType` to specify preorder or full order. */
    CHECKOUT: 'checkout',
    /** Opens a contact method picker where buyers choose how to reach the dealer. */
    CONTACT_PICKER: 'contact_picker',
    /** Opens a modal containing an embedded iframe. Requires `iframeUrl` on the action. */
    IFRAME_MODAL: 'iframe_modal',
};
const ctaActionTypeValues = [
    CtaActionType.MODAL,
    CtaActionType.URL,
    CtaActionType.CHECKOUT,
    CtaActionType.CONTACT_PICKER,
    CtaActionType.IFRAME_MODAL,
];
const CtaActionTypeSchema = z.enum(ctaActionTypeValues);
/**
 * Checkout order types available when action type is `checkout`.
 */
const CheckoutType = {
    PREORDER: 'preorder',
    FULL_ORDER: 'full_order',
};
const checkoutTypeValues = [CheckoutType.PREORDER, CheckoutType.FULL_ORDER];
const CheckoutTypeSchema = z.enum(checkoutTypeValues);
// ============================================================================
// Contact Methods
// ============================================================================
/**
 * Contact methods available in the contact picker CTA.
 * Direct methods (CALL, EMAIL, TEXT) open native device handlers.
 * Scheduled/request methods open the inquiry form or scheduling flow.
 */
const ContactMethod = {
    /** Opens tel: link */
    CALL: 'call',
    /** Opens mailto: link */
    EMAIL: 'email',
    /** Opens sms: link */
    TEXT: 'text',
    SCHEDULE_CALL: 'schedule_call',
    SCHEDULE_VISIT: 'schedule_visit',
    /** Submits a contact request via inquiry form */
    BE_CALLED: 'be_called',
    /** Submits a contact request via inquiry form */
    BE_EMAILED: 'be_emailed',
    /** Submits a contact request via inquiry form */
    BE_TEXTED: 'be_texted',
};
const contactMethodValues = [
    ContactMethod.CALL,
    ContactMethod.EMAIL,
    ContactMethod.TEXT,
    ContactMethod.SCHEDULE_CALL,
    ContactMethod.SCHEDULE_VISIT,
    ContactMethod.BE_CALLED,
    ContactMethod.BE_EMAILED,
    ContactMethod.BE_TEXTED,
];
const ContactMethodSchema = z.enum(contactMethodValues);
// ============================================================================
// CTA Placement Locations
// ============================================================================
/**
 * CTA placement locations
 */
const CTA_PLACEMENTS = {
    VDP_PAYMENT: 'vdp_payment',
    VDP_STICKY_TOP: 'vdp_sticky_top',
    VDP_STICKY_BOTTOM: 'vdp_sticky_bottom',
    VEHICLE_CARD: 'vehicle_card',
    BANNER: 'banner',
};
const ctaPlacementValues = [
    CTA_PLACEMENTS.VDP_PAYMENT,
    CTA_PLACEMENTS.VDP_STICKY_TOP,
    CTA_PLACEMENTS.VDP_STICKY_BOTTOM,
    CTA_PLACEMENTS.VEHICLE_CARD,
    CTA_PLACEMENTS.BANNER,
];
const CtaPlacementSchema = z.enum(ctaPlacementValues);
// ============================================================================
// CTA Schemas
// ============================================================================
const ctaActionSchema = z.object({
    type: CtaActionTypeSchema,
    formKey: FormKeySchema.optional(),
    url: urlSchema.optional(),
    openInNewTab: z.boolean().optional(), // Default: true for external URLs
    /** Required when type is `checkout`. Specifies whether this is a preorder or full purchase flow. */
    checkoutType: CheckoutTypeSchema.optional(),
    /** Required when type is `contact_picker`. Specifies which contact methods to show. */
    contactMethods: z.array(ContactMethodSchema).optional(),
    /** Required when type is `iframe_modal`. The URL to embed in the iframe. */
    iframeUrl: urlSchema.optional(),
});
const ctaContainerCopySchema = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
});
/**
 * Conditions that control whether a CTA is rendered in a given context.
 * All specified conditions must be satisfied (AND semantics).
 * Frontend resolves these conditions at render time.
 */
const ctaVisibilityConditionsSchema = z.object({
    /** Only show this CTA for vehicles in these categories */
    vehicleCategories: z.array(z.nativeEnum(VehicleCategory)).optional(),
    /** Only show this CTA when the item is marked for online sale */
    requiresOnlineSale: z.boolean().optional(),
    /** Only show this CTA when checkout is supported (i.e. checkout flow is available) */
    requiresCheckout: z.boolean().optional(),
});
/**
 * Schema for a single CTA item. Children cannot nest further —
 * only the top-level `CtaItemConfig` holds a flat `items` array.
 */
const ctaItemChildSchema = z.object({
    id: z.string(),
    label: z.string(),
    action: ctaActionSchema,
    icon: z.string().optional(),
    containerCopy: ctaContainerCopySchema.optional(),
    isHidden: z.boolean().optional(),
    /** Conditions that must be met for this CTA to be rendered. */
    visibilityConditions: ctaVisibilityConditionsSchema.optional(),
});
/**
 * Top-level CTA item for a placement.
 *
 * The top-level item acts as a container/trigger. Its `action` field is used
 * when there are no nested `items` (single-CTA placements like `vehicle_card`).
 * When `items` is present the top-level label/action describes the collapsed
 * trigger (e.g. a "More options" dropdown) and the children are the actual CTAs.
 *
 * When the number of visible CTAs exceeds the per-section limit, overflow
 * items are rendered in a "More" dropdown automatically.
 */
const ctaItemConfigSchema = ctaItemChildSchema.extend({
    /** Flat list of CTA items for this placement. */
    items: z.array(ctaItemChildSchema).optional(),
    /**
     * When true, all visible items are rendered even if they exceed the
     * per-section limit — extras go into a "More" dropdown.
     * When false or omitted, items beyond CTA_SECTION_LIMITS are dropped.
     */
    allowOverflowExpansion: z.boolean().optional(),
});
// ============================================================================
// Per-section CTA limits
// ============================================================================
/**
 * Maximum number of visible CTAs rendered as full buttons per placement.
 * When exceeded, overflow items are rendered in a "More" dropdown.
 */
const CTA_SECTION_LIMITS = {
    [CTA_PLACEMENTS.VDP_PAYMENT]: 3,
    [CTA_PLACEMENTS.VDP_STICKY_TOP]: 2,
    [CTA_PLACEMENTS.VDP_STICKY_BOTTOM]: 2,
    [CTA_PLACEMENTS.VEHICLE_CARD]: 2,
    [CTA_PLACEMENTS.BANNER]: 2,
};
const ctaConfigSchema = z.record(CtaPlacementSchema, ctaItemConfigSchema);

export { CTA_PLACEMENTS, CTA_SECTION_LIMITS, CheckoutType, CheckoutTypeSchema, ContactMethod, ContactMethodSchema, CtaActionType, CtaActionTypeSchema, CtaPlacementSchema, checkoutTypeValues, contactMethodValues, ctaActionSchema, ctaActionTypeValues, ctaConfigSchema, ctaContainerCopySchema, ctaItemChildSchema, ctaItemConfigSchema, ctaPlacementValues, ctaVisibilityConditionsSchema };
//# sourceMappingURL=CtaConfig.js.map
